/* eslint-disable */
import { useState, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SessionContext } from '../../../../context/sessionContext';
import Logger from '../../../../logger';
import i18n from '../../../../localization/i18n';
import * as rk from '../../../../localization/resourceKeys';
import { useMyVW } from '../../../../context/myVWContext';
import { useMyVWQueryParams } from '../../../hooks/useMyVWQueryParams';
import { verifyMFA } from '../../../services/myVW';
import { MYVW_ACCOUNT_LINKED_SUCCESS } from '../../../../constants/BusinessSplunkEvent';

const MAX_ATTEMPTS = 5;

export interface UseMFAVerifyReturn {
  code: string;
  isError: boolean;
  accountLocked: boolean;
  failedAttempts: number;
  submitPending: boolean;
  rememberDevice: boolean;
  verifyError: string | null;
  inputRef: React.MutableRefObject<null>;
  handleCodeChange: (value: string) => void;
  handleVerify: () => Promise<void>;
  resetVerifyState: () => void;
  setRememberDevice: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useMFAVerify(componentName: string): UseMFAVerifyReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const { partyId, applyAuthState } = useMyVW();
  const { token, brand, country, buildSearch } = useMyVWQueryParams();
  const { sessionDetails } = useContext(SessionContext)!;
  const myVWSessionDetails = { ...sessionDetails, brand, country };
  const urlParams = new URLSearchParams(location.search);
  const shouldLinkAccount = urlParams.get('isLinked') === 'false';
  const verificationMethod: 'email' | 'authenticator' = componentName === 'EnterAppCode' || componentName === 'SetupAuthenticatorApp' ? 'authenticator' : 'email';

  const [code, setCode] = useState('');
  const [isError, setIsError] = useState(false);
  const [accountLocked, setAccountLocked] = useState(false);
  const [submitPending, setSubmitPending] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const inputRef = useRef(null);

  const resetVerifyState = () => {
    setIsError(false);
    setVerifyError(null);
    setAttempts(0);
  };

  const handleCodeChange = (value: string) => {
    const hasSpace = value.indexOf(' ') >= 0;
    if (value.length >= 6 && !hasSpace) {
      setCode(value);
      setIsError(false);
    } else {
      setCode('');
    }
  };

  const handleVerify = async () => {
    if (!code || accountLocked) return;
    setVerifyError(null);
    setSubmitPending(true);
    const result = await verifyMFA(
      token,
      code,
      partyId ?? undefined,
      rememberDevice,
      { ...myVWSessionDetails, shouldLinkAccount },
      verificationMethod,
    );
    setSubmitPending(false);
    if (!result.isSuccess) {
      const hasApiAttempts = typeof result.loginAttemptCount === 'number';
      const nextAttempts = hasApiAttempts ? result.loginAttemptCount! : attempts + 1;
      setAttempts(nextAttempts);
      if (result.isLocked || nextAttempts >= MAX_ATTEMPTS) {
        setIsError(false);
        setAccountLocked(true);
        Logger.info(`MFA - account locked`, `Location ==> myVWMfa [${componentName}]`, sessionDetails);
        return;
      }
      setIsError(true);
      setVerifyError(result.error ?? i18n.t(rk.ERROR_MYVW_MFA_FAILED));
      Logger.info(`MFA verify failed (attempt ${nextAttempts})`, `Location ==> myVWMfa [${componentName}]`, sessionDetails);
      return;
    }

    const resolvedVwfsToken = result.vwfsToken ?? '';
    const resolvedAccountNumber = result.accountNumber ?? '';
    if (!resolvedVwfsToken || !resolvedAccountNumber) {
      setIsError(true);
      setVerifyError('Unable to complete sign in. Please try again.');
      Logger.info('MFA verify missing vwfsToken/accountNumber', `Location ==> myVWMfa [${componentName}]`, sessionDetails);
      return;
    }

    applyAuthState({
      vwfsToken: resolvedVwfsToken,
      accountNumber: resolvedAccountNumber,
    });

    if (shouldLinkAccount) {
      Logger.info(MYVW_ACCOUNT_LINKED_SUCCESS, '', sessionDetails);
    }

    Logger.info(`MFA verify successful`, `Location ==> myVWMfa [${componentName}]`, sessionDetails);
    navigate('/myVW/dashboard' + buildSearch({
      vwfsToken: resolvedVwfsToken,
      accountNumber: resolvedAccountNumber,
      token: '',
      loginAuthenticatorCode: '',
    }), { replace: true });
  };

  return {
    code,
    isError,
    accountLocked,
    failedAttempts: attempts,
    submitPending,
    rememberDevice,
    verifyError,
    inputRef,
    handleCodeChange,
    handleVerify,
    resetVerifyState,
    setRememberDevice,
  };
}
